"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Menu,
  LogOut,
  User,
  Users,
  Calculator,
  FileText,
  Settings,
  Heart,
  Home,
  Search,
  Megaphone,
  ShieldCheck,
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { FavoritesSheet } from "@/components/FavoritesSheet";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  isAuthMobileBottomNavPath,
  mobileBottomNavBarClassName,
  mobileBottomNavInnerClassName,
  mobileBottomNavItemClassName,
  SCROLL_TO_SERVICES_FLAG_KEY,
  usesHomeMobileBottomNav,
} from "@/lib/mobile-bottom-nav";
import { BrandLogo } from "@/components/BrandLogo";
import { MarketplaceFavoritesSheet } from "@/components/home/MarketplaceFavoritesSheet";
import { MarketplaceMobileBottomNav } from "@/components/home/MarketplaceMobileBottomNav";
import { useFavoriteServices } from "@/hooks/useFavoriteServices";

/** Full-width sticky bar; inner content capped at 1440px in the layout below. */
function navBarClassName(isScrolled: boolean) {
  return cn(
    "fixed top-0 left-0 right-0 z-50 w-full transition-colors duration-300",
    isScrolled
      ? "border-b border-primary/15 bg-card/90 backdrop-blur-md supports-[backdrop-filter]:bg-card/80 dark:border-border dark:bg-background/90 dark:supports-[backdrop-filter]:bg-background/75"
      : "border-b border-transparent bg-transparent"
  );
}

const navInnerClassName = "mx-auto w-full max-w-[1440px] px-3";

function desktopNavTextLinkClass(active: boolean) {
  return cn(
    "relative inline-block rounded-sm transition-colors duration-200",
    active
      ? "font-bold text-black dark:text-foreground"
      : "font-medium text-black/75 hover:text-black dark:text-muted-foreground dark:hover:text-foreground"
  );
}

function activeNavDotClass(active: boolean) {
  return cn(
    "pointer-events-none absolute left-1/2 top-full mt-1 h-1 w-1 -translate-x-1/2 rounded-full",
    active ? "bg-black dark:bg-foreground" : "opacity-0"
  );
}

/** Bottom app nav (with home in the middle) only on these routes when logged in */
const PLANNER_APP_ROUTES = [
  "/profil",
  "/kalukator-budzeta",
  "/spisak-gostiju",
  "/zadaci",
  "/podesavanja",
] as const;

const appNavItems: { label: string; href: string; icon: LucideIcon }[] = [
  { label: "Profil", href: "/profil", icon: User },
  { label: "Gosti", href: "/spisak-gostiju", icon: Users },
  { label: "Budžet", href: "/kalukator-budzeta", icon: Calculator },
  { label: "Zadaci", href: "/zadaci", icon: FileText },
  { label: "Podešavanja", href: "/podesavanja", icon: Settings },
];

const publicLinks = [
  { label: "Početna", href: "/", icon: Home },
  { label: "Usluge", href: "/#usluge", icon: Search },
  { label: "Objavi uslugu", href: "/#objavi-uslugu", icon: Megaphone },
] as const;
const MOBILE_HOME_NAV_PREF_KEY = "planner-mobile-home-nav-enabled";

function pathMatchesPlannerRoute(pathname: string): boolean {
  return PLANNER_APP_ROUTES.some((r) => pathname === r);
}

function AuthMarketplaceBottomNav({
  enabled,
  pathname,
  profilHref,
  favCount,
  favoritesOpen,
  onFavoritesOpenChange,
  onGoToServices,
  onOpenSearch,
}: {
  enabled: boolean;
  pathname: string;
  profilHref: string;
  favCount: number;
  favoritesOpen: boolean;
  onFavoritesOpenChange: (open: boolean) => void;
  onGoToServices: () => void;
  onOpenSearch: () => void;
}) {
  if (!enabled) return null;

  return (
    <>
      <MarketplaceMobileBottomNav
        favCount={favCount}
        profilHref={profilHref}
        profilActive={pathname === profilHref}
        onGoToServices={onGoToServices}
        onOpenFavorites={() => onFavoritesOpenChange(true)}
        onOpenSearch={onOpenSearch}
      />
      <MarketplaceFavoritesSheet
        open={favoritesOpen}
        onOpenChange={onFavoritesOpenChange}
      />
    </>
  );
}

export function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [authFavoritesOpen, setAuthFavoritesOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileHomeNavEnabled, setMobileHomeNavEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(MOBILE_HOME_NAV_PREF_KEY) !== "0";
  });
  const { user, logout, isLoading } = useAuth();
  const { favoriteIds } = useFavoriteServices();
  const pathname = usePathname();
  const router = useRouter();
  const [favoritesUiReady, setFavoritesUiReady] = useState(false);

  useEffect(() => {
    setFavoritesUiReady(true);
  }, []);

  const favCount = favoritesUiReady ? favoriteIds.size : 0;
  const showAuthMarketplaceBottomNav =
    mobileHomeNavEnabled && isAuthMobileBottomNavPath(pathname);
  const authProfilHref = user ? "/profil" : "/prijava";

  const goToHomeServices = useCallback(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(SCROLL_TO_SERVICES_FLAG_KEY, "1");
    }
    router.push("/#usluge");
  }, [router]);

  const isAdmin = user?.email?.toLowerCase() === "colovicsrdjan@yahoo.com";

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinkActive = (href: string) => {
    if (href.startsWith("/#")) return false;
    if (href === "/") return pathname === "/";
    return pathname === href;
  };
  const showHomeMobileNavToggle = usesHomeMobileBottomNav(pathname);
  const hideTopMobileFavoritesOnHome =
    mobileHomeNavEnabled &&
    (pathname === "/" ||
      pathname.startsWith("/usluge/") ||
      isAuthMobileBottomNavPath(pathname));

  const setHomeMobileNavPreference = useCallback((enabled: boolean) => {
    setMobileHomeNavEnabled(enabled);
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      MOBILE_HOME_NAV_PREF_KEY,
      enabled ? "1" : "0"
    );
    window.dispatchEvent(
      new CustomEvent("planner:mobile-nav-preference", {
        detail: { enabled },
      })
    );
  }, []);

  const handleLogout = () => {
    logout();
    router.push("/prijava");
    setIsMobileMenuOpen(false);
  };

  /** Loading: same nav shell as logged-in / logged-out (lg) */
  if (isLoading) {
    return (
      <>
        <div aria-hidden className="h-16" />
        <nav className={navBarClassName(isScrolled)}>
          <div className={navInnerClassName}>
            <div className="relative flex h-16 items-center">
              <Link
                href="/"
                className="relative z-10 flex shrink-0 items-center py-1"
              >
                <BrandLogo priority />
              </Link>
              <div className="relative z-10 ml-auto flex shrink-0 items-center gap-2">
                <FavoritesSheet />
                <ThemeToggle />
              </div>
            </div>
          </div>
        </nav>
      </>
    );
  }

  /* ——— Logged out ——— */
  if (!user) {
    return (
      <>
      <div aria-hidden className="h-16" />
      <nav className={navBarClassName(isScrolled)}>
        <div className={navInnerClassName}>
          <div className="relative flex h-16 items-center">
            <Link
              href="/"
              className="relative z-10 flex shrink-0 items-center py-1 motion-safe:transition-transform motion-safe:hover:scale-105 motion-safe:active:scale-95"
            >
              <BrandLogo priority />
            </Link>

            {/* Desktop — links centered in bar */}
            <div className="pointer-events-none absolute inset-0 hidden items-center justify-center lg:flex">
              <nav
                className="pointer-events-auto flex items-center gap-8"
                aria-label="Glavna navigacija"
              >
                {publicLinks.map((item) => {
                  const active = navLinkActive(item.href);
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={cn("relative", desktopNavTextLinkClass(active))}
                    >
                      <span>{item.label}</span>
                      <span
                        className={activeNavDotClass(active)}
                        aria-hidden
                      />
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="relative z-10 ml-auto flex shrink-0 items-center gap-2">
              <div className="hidden items-center gap-2 lg:flex">
                <FavoritesSheet />
                <ThemeToggle />
                <Button asChild variant="default">
                  <Link href="/prijava">Organizuj proslavu</Link>
                </Button>
              </div>

              {/* Mobile: theme + hamburger (all links in sheet) */}
              <div className="flex items-center gap-2 lg:hidden">
                {!hideTopMobileFavoritesOnHome && (
                  <FavoritesSheet triggerClassName="h-12 w-12 -ml-1" />
                )}
                <ThemeToggle />
                <Sheet
                  open={isMobileMenuOpen}
                  onOpenChange={setIsMobileMenuOpen}
                >
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-12 w-12 -mr-3"
                    >
                      <Menu className="min-h-[24px] min-w-[24px]" />
                      <span className="sr-only">Otvori navigaciju</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-[300px] sm:w-[400px] p-6"
                  >
                    <SheetTitle className="sr-only">Navigacija</SheetTitle>
                    <div className="flex h-full flex-col pt-10">
                      <nav className="flex-1 space-y-3 overflow-y-auto">
                        {publicLinks.map((item) => {
                          const Icon = item.icon;
                          const active = navLinkActive(item.href);
                          return (
                            <Link
                              key={item.href}
                              href={item.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <Button
                                variant={active ? "default" : "ghost"}
                                className="h-12 w-full justify-start gap-3 text-base [&_svg]:text-inherit"
                              >
                                <Icon className="h-6 w-6" />
                                {item.label}
                              </Button>
                            </Link>
                          );
                        })}
                        <Button
                          asChild
                          variant={pathname === "/prijava" ? "default" : "ghost"}
                          className="mt-2 h-12 w-full justify-start gap-3 text-base [&_svg]:text-inherit"
                        >
                          <Link
                            href="/prijava"
                            className="flex w-full items-center gap-3"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <Heart className="h-6 w-6 shrink-0" />
                            Organizuj proslavu
                          </Link>
                        </Button>
                      </nav>

                      {showHomeMobileNavToggle && (
                        <div className="mt-4 border-t border-border pt-4">
                          <Label
                            htmlFor="mobile-home-navigation-toggle-guest"
                            className="flex items-center justify-between gap-3 rounded-lg border border-border/70 px-3 py-2 text-sm font-medium"
                          >
                            <span>Mobilna navigacija</span>
                            <Switch
                              id="mobile-home-navigation-toggle-guest"
                              checked={mobileHomeNavEnabled}
                              onCheckedChange={setHomeMobileNavPreference}
                            />
                          </Label>
                        </div>
                      )}
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <AuthMarketplaceBottomNav
        enabled={showAuthMarketplaceBottomNav}
        pathname={pathname}
        profilHref={authProfilHref}
        favCount={favCount}
        favoritesOpen={authFavoritesOpen}
        onFavoritesOpenChange={setAuthFavoritesOpen}
        onGoToServices={goToHomeServices}
        onOpenSearch={goToHomeServices}
      />
      </>
    );
  }

  /* ——— Logged in ——— */
  const desktopItems: { label: string; href: string; icon: LucideIcon }[] = [
    ...publicLinks.map((p) => ({ label: p.label, href: p.href, icon: p.icon })),
    ...appNavItems,
  ];

  const sheetItemsLoggedIn = desktopItems;

  const desktopNavLinksOnly = desktopItems.filter(
    (item) => item.href !== "/podesavanja"
  );
  const settingsNavItem = desktopItems.find(
    (item) => item.href === "/podesavanja"
  );
  const SettingsNavIcon = settingsNavItem?.icon ?? Settings;

  const showMobileBottomNav = pathMatchesPlannerRoute(pathname);
  const bottomNavFive: { label: string; href: string; icon: LucideIcon }[] = [
    { label: "Profil", href: "/profil", icon: User },
    { label: "Gosti", href: "/spisak-gostiju", icon: Users },
    { label: "Početna", href: "/", icon: Home },
    { label: "Budžet", href: "/kalukator-budzeta", icon: Calculator },
    { label: "Zadaci", href: "/zadaci", icon: FileText },
  ];

  return (
    <>
      <div aria-hidden className="h-16" />
      <nav className={navBarClassName(isScrolled)}>
        <div className={navInnerClassName}>
          <div className="relative flex h-16 items-center">
            <Link
              href="/"
              className="relative z-10 flex shrink-0 items-center py-1 motion-safe:transition-transform motion-safe:hover:scale-105 motion-safe:active:scale-95"
            >
              <BrandLogo priority />
            </Link>

            {/* Desktop — text links centered in bar */}
            <div className="pointer-events-none absolute inset-0 hidden items-center justify-center lg:flex">
              <nav
                className="pointer-events-auto flex items-center gap-8"
                aria-label="Glavna navigacija"
              >
                {desktopNavLinksOnly.map((item) => {
                  const active = navLinkActive(item.href);
                  return (
                    <Link
                      key={`${item.href}-${item.label}`}
                      href={item.href}
                      className={cn("relative", desktopNavTextLinkClass(active))}
                    >
                      <span>{item.label}</span>
                      <span
                        className={activeNavDotClass(active)}
                        aria-hidden
                      />
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="relative z-10 ml-auto flex shrink-0 items-center gap-2">
              <div className="hidden items-center gap-2 lg:flex">
                {settingsNavItem ? (
                  <div
                    key={`${settingsNavItem.href}-${settingsNavItem.label}`}
                    className="relative"
                  >
                    <Popover
                      open={settingsMenuOpen}
                      onOpenChange={setSettingsMenuOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "h-10 w-10",
                            navLinkActive(settingsNavItem.href)
                              ? "text-black dark:text-foreground"
                              : "text-black/75 hover:text-black dark:text-muted-foreground dark:hover:text-foreground"
                          )}
                          aria-expanded={settingsMenuOpen}
                          aria-label={settingsNavItem.label}
                        >
                          <SettingsNavIcon className="h-5 w-5 min-h-[24px] min-w-[24px]" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-56 p-1">
                        {isAdmin && (
                          <Link
                            href="/admin"
                            className="block"
                            onClick={() => setSettingsMenuOpen(false)}
                          >
                            <Button
                              variant="ghost"
                              className="w-full justify-start gap-2"
                            >
                              <ShieldCheck className="h-4 w-4" />
                              Admin
                            </Button>
                          </Link>
                        )}
                        <Link
                          href="/podesavanja"
                          className="block"
                          onClick={() => setSettingsMenuOpen(false)}
                        >
                          <Button
                            variant="ghost"
                            className="w-full justify-start gap-2"
                          >
                            <Settings className="h-4 w-4" />
                            Podešavanja
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          className="w-full justify-start gap-2 text-destructive hover:text-destructive"
                          onClick={() => {
                            setSettingsMenuOpen(false);
                            handleLogout();
                          }}
                        >
                          <LogOut className="h-4 w-4" />
                          Odjavi se
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>
                ) : null}
                <FavoritesSheet />
                <ThemeToggle />
              </div>

              {/* Mobile top: theme + full menu */}
              <div className="flex items-center gap-2 lg:hidden">
                {!hideTopMobileFavoritesOnHome && (
                  <FavoritesSheet triggerClassName="h-12 w-12 -ml-1" />
                )}
                <ThemeToggle />
                <Sheet
                  open={isMobileMenuOpen}
                  onOpenChange={setIsMobileMenuOpen}
                >
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-12 w-12 -mr-3"
                    >
                      <Menu className="min-h-[24px] min-w-[24px]" />
                      <span className="sr-only">Otvori navigaciju</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-[300px] sm:w-[400px] p-6"
                  >
                    <SheetTitle className="sr-only">Navigacija</SheetTitle>
                    <div className="flex h-full flex-col pt-10">
                      <nav className="max-h-[calc(100vh-15rem)] flex-1 space-y-3 overflow-y-auto">
                        {sheetItemsLoggedIn.map((item) => {
                          const Icon = item.icon;
                          const active = navLinkActive(item.href);
                          return (
                            <Link
                              key={`${item.href}-${item.label}`}
                              href={item.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <Button
                                variant={active ? "default" : "ghost"}
                                className="h-12 w-full justify-start gap-3 text-base [&_svg]:text-inherit"
                              >
                                <Icon className="h-6 w-6" />
                                {item.label}
                              </Button>
                            </Link>
                          );
                        })}
                      </nav>

                      <div className="mt-4 space-y-3 border-t border-border pt-4">
                        {isAdmin && (
                          <Link
                            href="/admin"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <Button
                              variant={pathname === "/admin" ? "default" : "ghost"}
                              className="h-12 w-full justify-start gap-3 text-base [&_svg]:text-inherit"
                            >
                              <ShieldCheck className="h-6 w-6" />
                              Admin
                            </Button>
                          </Link>
                        )}
                        <Button
                          variant="destructive"
                          onClick={() => {
                            handleLogout();
                            setIsMobileMenuOpen(false);
                          }}
                          className="h-12 w-full justify-start gap-3 text-base"
                        >
                          <LogOut className="h-6 w-6" />
                          Odjavi se
                        </Button>

                        {showHomeMobileNavToggle && (
                          <Label
                            htmlFor="mobile-home-navigation-toggle"
                            className="flex items-center justify-between gap-3 rounded-lg border border-border/70 px-3 py-2 text-sm font-medium"
                          >
                            <span>Mobilna navigacija</span>
                            <Switch
                              id="mobile-home-navigation-toggle"
                              checked={mobileHomeNavEnabled}
                              onCheckedChange={setHomeMobileNavPreference}
                            />
                          </Label>
                        )}
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile bottom: only on planner app pages, Početna in the center */}
      {showMobileBottomNav && (
        <nav
          className={mobileBottomNavBarClassName}
          aria-label="Mobilna navigacija"
        >
          <div className={cn(mobileBottomNavInnerClassName, "grid-cols-5")}>
            {bottomNavFive.map((item) => {
              const Icon = item.icon;
              const active =
                item.href === "/" ? pathname === "/" : pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={mobileBottomNavItemClassName(active)}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="max-w-full truncate text-center text-[10px] font-medium">
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      <AuthMarketplaceBottomNav
        enabled={showAuthMarketplaceBottomNav}
        pathname={pathname}
        profilHref={authProfilHref}
        favCount={favCount}
        favoritesOpen={authFavoritesOpen}
        onFavoritesOpenChange={setAuthFavoritesOpen}
        onGoToServices={goToHomeServices}
        onOpenSearch={goToHomeServices}
      />
    </>
  );
}
